import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import Iframe from "../../../components/Iframe";
import Breadcrumbs from "../../../components/Breadcrumbs";
import {
  handleInputChangeWithScroll,
  MESSAGE_TYPES,
} from "../../../utils/editUtils";
import MediaSelector from "../../../components/website/MediaSelector";
import MediaGallery from "../../../components/website/MediaGallery";
import { MediaProvider, useMedia } from "../../../contexts/MediaContext";
import { useParams, useNavigate } from "react-router-dom";
import ResizableEditArea from "../../../components/common/ResizableEditArea";
import {
  executeValidation,
  handleInputChangeWithValidation,
  handleResetWithValidation,
  clearValidationErrors,
} from "../../../utils/formValidation";

function NewsEditPageContent() {
  const { newsId } = useParams();
  const navigate = useNavigate();
  const { isMediaOpen, fetchMediaFiles, selectMedia } = useMedia();
  const contentKey = "newsContent"; // ページのキー
  const [newsList, setNewsList] = useState([]); // ニュース一覧
  const [pageContent, setPageContent] = useState({});
  const [originalSlug, setOriginalSlug] = useState(newsId); // Iframe表示用の元のslug（newsId）
  const [message, setMessage] = useState(""); // メッセージ
  const [loadingText, setLoadingText] = useState("");
  const [hasChanges, setHasChanges] = useState(false);
  const [topImage, setTopImage] = useState(null); // top_image_id
  const [visualImages, setVisualImages] = useState([]); // image_ids
  const [isNewNews, setIsNewNews] = useState(false); // 新規作成モード
  const [breadcrumbs, setBreadcrumbs] = useState([]); // パンくず
  const [hashtagMasterList, setHashtagMasterList] = useState([]); // ハッシュタグマスタ
  const [hashtagInput, setHashtagInput] = useState(""); // ハッシュタグ入力値
  const [suggestions, setSuggestions] = useState([]); // 予測候補
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1); // 選択中の予測候補インデックス
  const [isComposing, setIsComposing] = useState(false); // 日本語入力中かどうか
  const [suggestionPosition, setSuggestionPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  }); // サジェスト位置

  useEffect(() => {
    if (newsId) return;

    setBreadcrumbs([
      { title: "Home", path: `/` },
      { title: "News", path: `/news` },
    ]);
  }, [newsId]);

  // messageが文字列の場合、3秒後に自動で消去
  useEffect(() => {
    if (message && message.trim() !== "") {
      const timer = setTimeout(() => {
        setMessage("");
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [message]);

  // ファイルアップロードハンドラー
  const handleDrop = useCallback(
    async (e) => {
      if (e.preventDefault) {
        e.preventDefault();
      }
      
      setLoadingText("アップロード中");
      
      const file = e.target?.files?.[0] || e.dataTransfer?.files?.[0];
      
      if (!file) {
        setLoadingText("");
        return;
      }

      try {
        const formData = new FormData();
        formData.append("images", file);
        const apiUrl = process.env.REACT_APP_API_URL;
        const response = await fetch(`${apiUrl}/api/image_upload/`, {
          method: "POST",
          credentials: "include",
          body: formData,
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.message);

        // アップロード完了後に選択状態にする
        if (data.images?.[0]) {
          // APIレスポンスをMediaGallery形式に変換
          const uploadedImage = {
            id: data.images[0].id,
            image_url: data.images[0].url || data.images[0].image_url,
            mime_type: data.images[0].mime_type,
            width: data.images[0].width,
            height: data.images[0].height,
            file_size: data.images[0].file_size,
          };
          
          // メディアファイル一覧を再取得
          await fetchMediaFiles();
          
          // fetchMediaFiles完了後に選択状態にする
          selectMedia(uploadedImage, true);
        }
      } catch (error) {
        console.error("アップロードエラー:", error);
      }
      setLoadingText("");
    },
    [fetchMediaFiles, selectMedia, setLoadingText]
  );

  // ハッシュタグマスタの取得
  const fetchHashtagMasterList = useCallback(async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_hashtag_master/`, {
        method: "GET",
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();

      setHashtagMasterList(data);
    } catch (error) {
      console.error("ハッシュタグマスタの取得に失敗しました", error);
    }
  }, []);

  // ニュースリストの取得
  const fetchNewsList = async () => {
    setLoadingText("読み込み中");
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_news_list/`, {
        method: "GET",
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();

      setNewsList(data);
      setLoadingText("");
    } catch (error) {
      console.error("ニュースリストの取得に失敗しました", error);
      setLoadingText("");
    }
  };

  // ニュースデータの取得
  const fetchNewsData = useCallback(async () => {
    if (!newsId || newsId === "new") return;

    setLoadingText("読み込み中");
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_news/`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          news_id: newsId,
        }),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();

      setPageContent(data);
      setOriginalSlug(data.id); // Iframe表示用の元のslugを保持
      setTopImage(data.top_image);
      setVisualImages(data.images);
      setBreadcrumbs([
        { title: "Home", path: `/` },
        { title: "News", path: `/news` },
        { title: `${data.title}`, path: `/news/${data.id}` },
      ]);
      setHasChanges(false);

      // iframeにも通知して初期データを設定
      setTimeout(() => {
        const iframe = document.querySelector("iframe");
        if (iframe && iframe.contentWindow) {
          iframe.contentWindow.postMessage(
            {
              type: MESSAGE_TYPES.TEXT_UPDATE,
              contentKey,
              newsContent: data,
              allData: { newsContent: data },
            },
            process.env.REACT_APP_ORIGIN
          );
        } else {
          console.warn(
            "iframe.contentWindow が存在しません（postMessage 送信失敗）"
          );
        }
      }, 100);
    } catch (error) {
      console.error("詳細の取得に失敗しました", error);
    }
    setLoadingText("");
  }, [newsId, contentKey]);

  // 初回のみ実行（メディアファイルとハッシュタグマスタ取得）
  useEffect(() => {
    fetchNewsList();
    fetchMediaFiles();
    fetchHashtagMasterList();
  }, [fetchMediaFiles, fetchHashtagMasterList]);

  // newsIdが変更された時に実行（新規作成モードまたは編集モードの設定）
  useEffect(() => {
    // バリデーションエラーをクリア
    clearValidationErrors();
    
    if (newsId === "new") {
      // 新規作成モードに設定
      setIsNewNews(true);
      const newPageContent = {
        title: "",
        text: "",
        hashtags: [],
      };
      setPageContent(newPageContent);
      setOriginalSlug("new");
      setTopImage(null);
      setVisualImages([]);
      setBreadcrumbs([
        { title: "Home", path: `/` },
        { title: "News", path: `/news` },
        { title: "Add News", path: `/news/new` },
      ]);
      setHasChanges(false);

      // 新規作成モードの場合、iframeも初期化
      setTimeout(() => {
        const iframe = document.querySelector("iframe");
        if (iframe && iframe.contentWindow) {
          iframe.contentWindow.postMessage(
            {
              type: MESSAGE_TYPES.TEXT_UPDATE,
              contentKey,
              newsContent: newPageContent,
              allData: { newsContent: newPageContent },
            },
            process.env.REACT_APP_ORIGIN
          );
        }
      }, 100);
    } else if (newsId) {
      // 編集モードに設定
      setIsNewNews(false);
      fetchNewsData();
    }
  }, [newsId, fetchNewsData]);

  // ビジュアル画像変更ハンドラー
  const handleVisualImagesChange = useCallback(
    (images) => {
      setHasChanges(true);

      // imagesが1枚以上の場合、1枚目をtopImageに、残りをvisualImagesにセット
      if (images.length > 0) {
        setTopImage(images[0]);
        setVisualImages(images.slice(1));
      } else {
        setTopImage(null);
        setVisualImages([]);
      }

      // iframeに通知
      setTimeout(() => {
        const iframe = document.querySelector("iframe");
        if (iframe && iframe.contentWindow) {
          // トップ画像の更新
          if (images.length > 0) {
            iframe.contentWindow.postMessage(
              {
                type: MESSAGE_TYPES.IMAGE_UPDATE,
                target: `.edit_top_image`,
                contentKey,
                fieldName: "top_image",
                value: images[0],
                allData: {
                  newsContent: { ...pageContent, top_image: images[0] },
                },
              },
              process.env.REACT_APP_ORIGIN
            );
          } else {
            iframe.contentWindow.postMessage(
              {
                type: MESSAGE_TYPES.IMAGE_UPDATE,
                target: `.edit_top_image`,
                contentKey,
                fieldName: "top_image",
                value: [],
                allData: {
                  newsContent: { ...pageContent, top_image: images[0] },
                },
              },
              process.env.REACT_APP_ORIGIN
            );
          }

          // ビジュアル画像の更新（1枚目以外）
          iframe.contentWindow.postMessage(
            {
              type: MESSAGE_TYPES.MULTIPLE_IMAGES_UPDATE,
              target: `.edit_images`,
              contentKey,
              fieldName: "images",
              value: images.slice(1),
            },
            process.env.REACT_APP_ORIGIN
          );
        }
      }, 100);
    },
    [contentKey]
  );

  // ニュースデータを保存（新規作成・更新）
  const handleNewsDataSubmit = async () => {
    // 保存するデータ
    const formData = {
      title: pageContent.title,
      text: pageContent.text,
      image_ids: visualImages.map((img) => img.id),
      top_image: topImage ? topImage.id : null,
      hashtags: pageContent.hashtags,
    };

    // 更新の場合はstudio_idを追加
    if (!isNewNews) {
      formData.news_id = newsId;
    }

    // バリデーション実行
    const allFormData = {
      ...pageContent,
    };

    if (!executeValidation(allFormData, validationRules, fieldLabels, setMessage)) {
      return;
    }

    try {
      setLoadingText(isNewNews ? "作成中" : "保存中");

      const endpoint = isNewNews ? "/api/create_news/" : "/api/update_news/";
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}${endpoint}`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(`${data.message} status: ${response.status}`);
      }

      setMessage(data.message);
      setHasChanges(false);

      if (isNewNews && data.news_id) {
        // 新規作成の場合、ニュースリストを更新してから作成されたニュースのページに遷移
        setIsNewNews(false);
        await fetchNewsList();
        navigate(`/news/${data.news_id}`);
      } else {
        // 更新の場合は最新データとニュースリストを取得
        await fetchNewsData();
        await fetchNewsList();
      }
    } catch (error) {
      console.error("There was a problem with the fetch operation:", error);
    }
    setLoadingText("");
  };

  // バリデーションルールを定義
  const validationRules = {
    title: { required: true },
    text: { required: true },
  };

  // フィールドラベルのマッピング
  const fieldLabels = {
    title: "タイトル",
    text: "本文",
  };

  // テキスト入力ハンドラー
  const handleInputChange = (e) => {
    handleInputChangeWithValidation(
      e,
      setPageContent,
      handleInputChangeWithScroll,
      {
        contentKey,
        currentData: pageContent,
        setData: setPageContent,
      }
    );
    setHasChanges(true);
  };

  // ハッシュタグ入力ハンドラー
  const handleHashtagInputChange = (e) => {
    let value = e.target.value;
    setHashtagInput(value);
    setSelectedSuggestionIndex(-1);

    if (value.trim()) {
      // valueの文頭が"#"の場合は、"#"を除外
      if (value.startsWith("#")) {
        value = value.slice(1);
      }
      // hashtagMasterListから予測候補を検索（文頭一致）
      const filtered = hashtagMasterList.filter((hashtag) =>
        hashtag.text.toLowerCase().startsWith(value.toLowerCase())
      );
      setSuggestions(filtered);

      // サジェストの位置を計算
      const inputRect = e.target.getBoundingClientRect();
      setSuggestionPosition({
        top: inputRect.bottom + window.scrollY,
        left: inputRect.left + window.scrollX,
        width: inputRect.width,
      });
    } else {
      setSuggestions([]);
    }
  };

  // ハッシュタグキーダウンハンドラー
  const handleHashtagKeyDown = (e) => {
    // 日本語入力中（IME変換中）の場合はエンターキーを無視
    if ((e.key === "Enter" && isComposing) || (e.key === " " && isComposing)) {
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedSuggestionIndex((prev) =>
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedSuggestionIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (selectedSuggestionIndex >= 0) {
        // サジェストが選択されている場合はサジェストを確定
        handleSuggestionSelect(suggestions[selectedSuggestionIndex]);
      } else if (hashtagInput.trim()) {
        // サジェストが選択されていない場合は入力内容を確定
        addHashtag(hashtagInput.trim());
      }
    } else if (e.key === " ") {
      e.preventDefault();
      if (hashtagInput.trim()) {
        addHashtag(hashtagInput.trim());
      }
    } else if (e.key === "Backspace" && hashtagInput === "") {
      e.preventDefault();
      // 入力欄が空の時のバックスペースで最後のハッシュタグを削除
      const currentHashtags = pageContent.hashtags || [];
      if (currentHashtags.length > 0) {
        const updatedHashtags = currentHashtags.slice(0, -1);
        setPageContent((prev) => ({
          ...prev,
          hashtags: updatedHashtags,
        }));
        setHasChanges(true);
        setTimeout(() => {
          // iframeにハッシュタグ変更を通知
          const iframe = document.querySelector("iframe");
          if (iframe && iframe.contentWindow) {
            iframe.contentWindow.postMessage(
              {
                type: MESSAGE_TYPES.HASHTAG_UPDATE,
                contentKey,
                hashtags: updatedHashtags,
              },
              process.env.REACT_APP_ORIGIN
            );
          }
        }, 100);
      }
    }
  };

  // 予測候補選択ハンドラー
  const handleSuggestionSelect = (suggestion) => {
    addHashtag(suggestion.text, suggestion.id);
  };

  // ハッシュタグ追加
  const addHashtag = (text, id = null) => {
    // #が含まれていない場合は先頭に追加
    const hashtagText = text.startsWith("#") ? text : `#${text}`;

    // 既に存在するかチェック
    const currentHashtags = pageContent.hashtags || [];
    const exists = currentHashtags.some(
      (hashtag) => `#${hashtag.text}` === hashtagText
    );

    if (!exists) {
      const newHashtag = {
        id: id || `new_${Date.now()}`,
        text: hashtagText,
      };

      const updatedHashtags = [...currentHashtags, newHashtag];
      setPageContent((prev) => ({
        ...prev,
        hashtags: updatedHashtags,
      }));
      setHasChanges(true);

      // iframeにハッシュタグ変更を通知
      setTimeout(() => {
        const iframe = document.querySelector("iframe");
        if (iframe && iframe.contentWindow) {
          iframe.contentWindow.postMessage(
            {
              type: MESSAGE_TYPES.HASHTAG_UPDATE,
              contentKey,
              hashtags: updatedHashtags,
            },
            process.env.REACT_APP_ORIGIN
          );
        }
      }, 100);
    }

    setHashtagInput("");
    setSuggestions([]);
    setSelectedSuggestionIndex(-1);
  };

  // ハッシュタグ削除ハンドラー
  const handleHashtagRemove = (index) => {
    const currentHashtags = pageContent.hashtags || [];
    const updatedHashtags = currentHashtags.filter((_, i) => i !== index);
    setPageContent((prev) => ({
      ...prev,
      hashtags: updatedHashtags,
    }));
    setHasChanges(true);

    // iframeにハッシュタグ変更を通知
    setTimeout(() => {
      const iframe = document.querySelector("iframe");
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage(
          {
            type: MESSAGE_TYPES.HASHTAG_UPDATE,
            contentKey,
            hashtags: updatedHashtags,
          },
          process.env.REACT_APP_ORIGIN
        );
      }
    }, 100);
  };

  // ニュース削除処理
  const handleDeleteNews = async (newsId, title) => {
    if (!window.confirm(`「${title}」を削除しますか？`)) {
      return;
    }

    setLoadingText("削除中");
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/delete_news/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          news_id: newsId,
        }),
      });

      if (!response.ok) {
        throw new Error("削除に失敗しました。");
      }

      // 削除成功時、リストを再取得
      await fetchNewsList();
      alert("削除しました。");
      setOriginalSlug("");
      navigate("/news");
      setLoadingText("");
    } catch (error) {
      console.error("削除中にエラーが発生しました:", error);
      alert("削除に失敗しました。");
      setLoadingText("");
    }
  };

  return (
    <>
      <ResizableEditArea>
        {({ onResizeStart }) => (
          <>
            {/* パンくず */}
            <Breadcrumbs
              breadcrumbs={breadcrumbs}
              onResizeStart={onResizeStart}
            />

            <div className={`message ${message ? "success" : ""}`}>
              <p>{message}</p>
            </div>

            <div className="form">
              {/* 記事一覧セクション */}
              <section>
                <div className="news-list">
                  <div className="item">
                    <p>記事一覧 ({newsList.length}件)</p>
                    {newsList.length === 0 ? (
                      <>
                        <p className="no-news">
                          記事がありません。新規記事を作成してください。
                        </p>
                        <div className="news-grid">
                          <div
                            className="news-card"
                            onClick={() => navigate("/news/new")}
                          >
                            <div className="news-card-content">
                              <p className="news-title add">Add New Post</p>
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="news-grid">
                        <div
                          className="news-card"
                          onClick={() => navigate("/news/new")}
                        >
                          <div className="news-card-content">
                            <p className="news-title add">Add New Post</p>
                          </div>
                        </div>
                        {newsList.map((news, index) => (
                          <div
                            key={news.id}
                            className={`news-card ${
                              String(newsId) === String(news.id)
                                ? "selected"
                                : ""
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/news/${news.id}`);
                            }}
                          >
                            <div className="news-card-content">
                              <p className="news-date">
                                {(() => {
                                  const date = new Date(news.created_at);
                                  const dateStr = date
                                    .toLocaleDateString("ja-JP", {
                                      year: "numeric",
                                      month: "2-digit",
                                      day: "2-digit",
                                    })
                                    .replace(/\//g, "-");
                                  const weekday = date
                                    .toLocaleDateString("ja-JP", {
                                      weekday: "short",
                                    })
                                    .replace(/[()]/g, "");
                                  return `${dateStr} ${weekday}`;
                                })()}
                              </p>
                              <p className="news-title">
                                {news.title || `記事${index + 1}`}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </section>

              {newsId && (
                <>
                  {/* 基本情報 */}
                  <section>
                    {!isNewNews && (
                      <div className="button-group">
                        <div className="action-section">
                          <button
                            type="button"
                            className="delete-btn"
                            onClick={() =>
                              handleDeleteNews(
                                pageContent.id,
                                pageContent.title
                              )
                            }
                            title="記事を削除"
                          >
                            <i className="fa-regular fa-trash"></i>
                          </button>
                        </div>
                      </div>
                    )}
                    <div className="area1">
                      {/* ニュースタイトル */}
                      <input
                        type="text"
                        name="title"
                        value={pageContent?.title || ""}
                        placeholder="タイトルを入力"
                        onChange={handleInputChange}
                        onFocus={handleInputChange}
                        data-label="タイトル"
                        data-required="true"
                      />

                      {/* ニュース本文 */}
                      <textarea
                        className="input2"
                        name="text"
                        value={pageContent?.text || ""}
                        placeholder="本文を入力"
                        onChange={handleInputChange}
                        onFocus={handleInputChange}
                        data-label="本文"
                        data-required="true"
                      />
                    </div>
                  </section>

                  {/* ビジュアル画像 */}
                  <section>
                    <div className="img-form">
                      <MediaSelector
                        label="ビジュアル画像"
                        aspectRatio="16:9"
                        type="image"
                        fieldName="visual_images"
                        selectedMedia={[
                          ...(topImage ? [topImage] : []),
                          ...visualImages,
                        ]}
                        onSelect={handleVisualImagesChange}
                        mode="multiple"
                      />
                    </div>
                  </section>

                  {/* ハッシュタグ */}
                  <section>
                    <div className="hashtag-form">
                      <div className="hashtag-input-container">
                        {pageContent.hashtags &&
                          pageContent.hashtags.map((hashtag, index) => (
                            <span
                              key={hashtag.id || `new_${index}`}
                              className="hashtag-tag"
                            >
                              {hashtag.text.startsWith("#")
                                ? hashtag.text
                                : `#${hashtag.text}`}
                              <button
                                type="button"
                                className="hashtag-remove"
                                onClick={() => handleHashtagRemove(index)}
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        <input
                          type="text"
                          className="hashtag-input"
                          placeholder="ハッシュタグを入力"
                          value={hashtagInput}
                          onChange={handleHashtagInputChange}
                          onKeyDown={handleHashtagKeyDown}
                          onCompositionStart={() => setIsComposing(true)}
                          onCompositionEnd={() => setIsComposing(false)}
                        />
                      </div>
                    </div>
                  </section>
                </>
              )}
            </div>

            {/* 送信ボタン */}
            <div className="submit-area">
              <button
                type="reset"
                onClick={
                  isNewNews
                    ? () => {
                        handleResetWithValidation(() => {
                          setPageContent({
                            title: "",
                            text: "",
                            hashtags: [],
                          });
                          setTopImage(null);
                          setVisualImages([]);
                          setHasChanges(false);
                        });
                      }
                    : () => handleResetWithValidation(fetchNewsData)
                }
                disabled={!hasChanges}
              >
                Reset
              </button>
              <button
                type="submit"
                onClick={handleNewsDataSubmit}
                disabled={!hasChanges}
              >
                Submit
              </button>
            </div>
            {/* メディア / オーバーレイ */}
            {isMediaOpen && <MediaGallery onUpload={handleDrop} />}
          </>
        )}
      </ResizableEditArea>
      {/* ローディング */}
      {loadingText && (
        <div className="overlay">
          <div className="loading">
            <div className="circle"></div>
            <p>{loadingText}...</p>
          </div>
        </div>
      )}
      {/* プレビュー */}
      <Iframe
        path={
          isNewNews
            ? "/news/new?secret=this_is_a_request_from_the_admin_page"
            : originalSlug
            ? `/news/${originalSlug}`
            : "/news/new?secret=this_is_a_request_from_the_admin_page"
        }
        data={pageContent}
        contentKey={contentKey}
      />

      {/* サジェストをポータルで最前面に表示 */}
      {suggestions.length > 0 &&
        createPortal(
          <div
            className="hashtag-suggestions-portal"
            style={{
              position: "absolute",
              top: suggestionPosition.top,
              left: suggestionPosition.left,
              width: suggestionPosition.width,
              backgroundColor: "#1a1a1a",
              border: "1px solid #ffffff50",
              borderRadius: "0 0 4px 4px",
              maxHeight: "200px",
              overflowY: "auto",
              zIndex: 999999,
            }}
          >
            {suggestions.map((suggestion, index) => (
              <div
                key={suggestion.id}
                className={`hashtag-suggestion ${
                  index === selectedSuggestionIndex ? "selected" : ""
                }`}
                style={{
                  padding: "0.5rem",
                  cursor: "pointer",
                  borderBottom: "1px solid #ffffff20",
                  transition: "background-color 0.2s",
                  backgroundColor:
                    index === selectedSuggestionIndex
                      ? "#76bbff"
                      : "transparent",
                  color: index === selectedSuggestionIndex ? "#000" : "#fff",
                }}
                onClick={() => handleSuggestionSelect(suggestion)}
              >
                {suggestion.text}
              </div>
            ))}
          </div>,
          document.body
        )}
    </>
  );
}

// MediaProviderでラップしたコンポーネントをエクスポート
export default function NewsEditPage() {
  return (
    <MediaProvider>
      <NewsEditPageContent />
    </MediaProvider>
  );
}
